'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ImageUp, Loader2, Plus, X } from 'lucide-react'

import { cn } from '@/utilities/ui'

import { Button } from './button'

const validateFile = (file: File): boolean => {
  // need to allow for Word, plain text
  const validTypes = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain',
  ]
  const maxSize = 15 * 1024 * 1024 // 15 MB

  if (!validTypes.includes(file.type)) {
    alert('Please select a JPEG, PNG, PDF, or WEBP image file.')
    return false
  }

  if (file.size > maxSize) {
    alert('File size must be less than 15 MB.')
    return false
  }

  return true
}

type UploadInputProps = {
  path: string
  relationTo: string
  /** When provided and uploadOnSubmit is false, file is uploaded immediately and id passed to onValueChange */
  onUpload?: (file: File) => Promise<{ id: number }>
  /** Called with the Media id (after upload) or the File (when uploadOnSubmit) or null when cleared */
  onValueChange?: (value: number | File | null) => void
  /** Current value: Media id, or File when uploadOnSubmit (upload happens on form submit) */
  value?: number | File | null
  /** When true, only store the selected File; do not upload until form is submitted */
  uploadOnSubmit?: boolean
  /** When this value changes (e.g. increment on form submit), internal state is cleared (selectedFile, uploadedFileName, file input) */
  resetSignal?: boolean
  uploadWidth?: number
  uploadHeight?: number
}

export const UploadInput = ({
  path,
  relationTo: _relationTo,
  onUpload,
  onValueChange,
  value = null,
  uploadOnSubmit = false,
  resetSignal,
  uploadWidth,
  uploadHeight,
}: UploadInputProps) => {
  const [isDragging, setIsDragging] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const valueIsFile = value instanceof File
  const fileNameFromValue = valueIsFile ? value.name : null
  // Treat empty string as no value (e.g. form defaultValues can set upload fields to '' by mistake)
  const effectiveValue = (value as unknown) === '' ? null : value

  const previewFile = selectedFile ?? (valueIsFile ? value : null)
  const isImagePreview = Boolean(previewFile?.type.startsWith('image/'))

  const previewUrl = useMemo(() => {
    if (!previewFile?.type.startsWith('image/')) return null
    return URL.createObjectURL(previewFile)
  }, [previewFile])

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  const handleRemove = () => {
    setSelectedFile(null)
    setUploadedFileName(null)
    onValueChange?.(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // When parent signals form submission completed (resetSignal changes), clear
  // internal state during render...
  const [prevResetSignal, setPrevResetSignal] = useState(resetSignal)
  if (resetSignal !== prevResetSignal) {
    setPrevResetSignal(resetSignal)
    if (resetSignal) {
      if (selectedFile) setSelectedFile(null)
      if (uploadedFileName) setUploadedFileName(null)
    }
  }

  // ...but notify the parent and touch the DOM input from an effect: calling
  // onValueChange (a parent setState) during render is not allowed by React.
  useEffect(() => {
    if (!resetSignal) return
    onValueChange?.(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [resetSignal, onValueChange])

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const processFile = useCallback(
    async (file: File) => {
      if (!validateFile(file)) return
      if (uploadOnSubmit && onValueChange) {
        setSelectedFile(file)
        onValueChange(file)
      } else if (onUpload && onValueChange) {
        setIsUploading(true)
        try {
          const doc = await onUpload(file)
          onValueChange(doc.id)
          setUploadedFileName(file.name)
          setSelectedFile(null)
        } catch (err) {
          console.error('Upload failed:', err)
          alert('Upload failed. Please try again.')
        } finally {
          setIsUploading(false)
        }
      } else {
        setSelectedFile(file)
        const dataTransfer = new DataTransfer()
        dataTransfer.items.add(file)
        if (fileInputRef.current) {
          fileInputRef.current.files = dataTransfer.files
        }
      }
    },
    [uploadOnSubmit, onUpload, onValueChange],
  )

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)
      const files = e.dataTransfer.files
      if (files?.length > 0) void processFile(files[0])
    },
    [processFile],
  )

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files
      if (files && files.length > 0) {
        void processFile(files[0])
      } else {
        if (!onValueChange) setSelectedFile(null)
      }
      if ((!files || files.length === 0) && onValueChange) {
        onValueChange(null)
        setUploadedFileName(null)
        if (fileInputRef.current) fileInputRef.current.value = ''
      }
    },
    [processFile, onValueChange],
  )

  const handleBrowseClick = () => {
    fileInputRef.current?.click()
  }

  const displayLabel =
    selectedFile?.name ?? uploadedFileName ?? (effectiveValue != null ? fileNameFromValue : null)
  const hasValue = effectiveValue != null || displayLabel

  return (
    <div className="w-full">
      <div
        className={cn(
          'relative overflow-hidden rounded-xl border transition-colors',
          isDragging
            ? 'border-primary bg-primary/5'
            : 'border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100',
          hasValue && 'border-gray-400 bg-gray-100',
          isImagePreview && previewUrl && 'bg-transparent hover:bg-transparent',
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
          onChange={handleFileChange}
          className="hidden"
          id={path}
          name={path}
        />
        {isUploading ? (
          <div className="flex flex-col items-center justify-center p-4 text-center">
            <div className="mt-4 flex items-center gap-2">
              <Loader2 className="size-5 animate-spin text-primary" />
              <p className="text-sm text-gray-700">Uploading…</p>
            </div>
          </div>
        ) : hasValue && previewUrl ? (
          <div className="relative w-full">
            {/* eslint-disable-next-line @next/next/no-img-element -- blob URL preview of local File */}
            <img
              src={previewUrl}
              alt={previewFile?.name ?? 'Selected image'}
              className="block h-auto max-w-full"
              style={{
                width: uploadWidth ? `${uploadWidth}px` : '100%',
                aspectRatio:
                  uploadWidth && uploadHeight ? (uploadWidth / uploadHeight).toFixed(2) : undefined,
              }}
            />
            <Button
              type="button"
              variant="icon"
              aria-label="Clear selection"
              onClick={handleRemove}
              className="absolute top-2 right-2 size-8 rounded-full shadow-md hover:bg-destructive hover:text-destructive-foreground"
            >
              <X className="size-4 stroke-1" />
            </Button>
          </div>
        ) : hasValue ? (
          <div className="relative w-full p-4 pr-12">
            <p className="overflow-hidden text-sm font-medium text-ellipsis text-gray-700">
              {selectedFile
                ? `Selected: ${selectedFile.name}`
                : `Uploaded: ${displayLabel ?? 'file'}`}
            </p>
            <Button
              type="button"
              variant="icon"
              aria-label="Clear selection"
              onClick={handleRemove}
              className="absolute top-2 right-2 size-8 rounded-full shadow-md hover:bg-destructive hover:text-destructive-foreground"
            >
              <X className="size-4 stroke-1" />
            </Button>
          </div>
        ) : (
          <div
            className="flex h-auto max-w-full flex-col items-center justify-center p-4 text-center"
            style={{
              width: uploadWidth ? `${uploadWidth}px` : '100%',
              aspectRatio:
                uploadWidth && uploadHeight ? (uploadWidth / uploadHeight).toFixed(2) : undefined,
            }}
          >
            <ImageUp
              className={cn(
                'mb-4 size-16 transition-colors',
                isDragging ? 'text-primary' : 'text-gray-400',
              )}
              strokeWidth={1}
            />
            <p className="mb-4 text-sm text-muted-foreground">
              Choose a file or drag and drop an image here. JPEG, PNG, WEBP, PDF, Word, and plain
              text formats accepted up to 15 MB.
            </p>
            <Button
              type="button"
              onClick={handleBrowseClick}
              customIcon={<Plus className="size-3 stroke-1 text-primary-foreground" />}
            >
              Browse
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
